// components/Sections/GlobalReachSection.tsx
"use client";

import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  alpha,
  Chip,
  Grid,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import { Public, LocationOn, Language, Explore } from "@mui/icons-material";

const GlobalReachSection = () => {
  const theme = useTheme();
  const [selectedRegion, setSelectedRegion] = useState<string | null>(
    "north-america"
  );

  // Focus on regions we're equipped to serve, not current client counts
  const targetRegions = [
    {
      id: "north-america",
      name: "North America",
      x: 200,
      y: 150,
      focus: "Tech Startups & SMEs",
      description:
        "Helping innovative businesses scale with custom software solutions",
      languages: ["English", "Spanish"],
      timezone: "EST/PST",
    },
    {
      id: "europe",
      name: "Europe",
      x: 400,
      y: 120,
      focus: "Digital Transformation",
      description: "Supporting businesses in their digital innovation journey",
      languages: ["English", "German", "French"],
      timezone: "GMT/CET",
    },
    {
      id: "asia",
      name: "Asia Pacific",
      x: 600,
      y: 180,
      focus: "Rapid Growth Markets",
      description:
        "Partnering with ambitious companies in fast-growing economies",
      languages: ["English", "Hindi", "Mandarin"],
      timezone: "IST/SGT",
    },
    {
      id: "africa",
      name: "Africa",
      x: 450,
      y: 250,
      focus: "Emerging Tech Ecosystems",
      description:
        "Empowering businesses in rapidly developing digital markets",
      languages: ["English", "French", "Arabic"],
      timezone: "WAT/EAT",
    },
    {
      id: "south-america",
      name: "South America",
      x: 280,
      y: 300,
      focus: "Latin American Innovation",
      description:
        "Enabling digital transformation across Latin American enterprises",
      languages: ["English", "Spanish", "Portuguese"],
      timezone: "BRT/ART",
    },
  ];

  const hubLocation = { x: 450, y: 180 };

  const handleLocationClick = (location: (typeof targetRegions)[0]) => {
    setSelectedRegion(location.id);
  };

  const getRegionDetails = (regionId: string) => {
    return targetRegions.find((loc) => loc.id === regionId);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.02
        )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Header - Focus on capability, not scale */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
            }}
          >
            Built for Global Impact
          </Typography>
          <Typography
            variant="h6"
            component={"h3"}
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto", mb: 4 }}
          >
            Ready to serve clients worldwide with remote-first expertise and
            24/7 development cycles
          </Typography>

          {/* Capability Stats - Not client counts */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{ fontWeight: 700, color: theme.palette.primary.main }}
              >
                24/7
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Development Coverage
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{ fontWeight: 700, color: theme.palette.primary.main }}
              >
                5+
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Languages Supported
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{ fontWeight: 700, color: theme.palette.primary.main }}
              >
                Remote
              </Typography>
              <Typography variant="body1" color="text.secondary">
                First Approach
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{ fontWeight: 700, color: theme.palette.primary.main }}
              >
                Global
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Team Mindset
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={6} alignItems="center">
          {/* Map Visualization */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.95
                )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 3,
                overflow: "hidden",
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  component={"h3"}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
                >
                  <Explore color="primary" />
                  Global Service Capability
                </Typography>

                {/* Interactive Map SVG */}
                <Box
                  sx={{
                    position: "relative",
                    height: 300,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.03
                    )} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
                    borderRadius: 2,
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    mb: 3,
                    overflow: "hidden",
                  }}
                >
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 800 400"
                    style={{ cursor: "pointer" }}
                  >
                    {/* Simple world map outline */}
                    <path
                      d="M150,100 Q200,80 250,100 Q300,120 350,110 Q400,100 450,120 Q500,140 550,130 Q600,120 650,140 L650,250 Q600,270 550,260 Q500,250 450,270 Q400,290 350,280 Q300,270 250,290 Q200,310 150,300 Z"
                      fill={alpha(theme.palette.primary.main, 0.05)}
                      stroke={alpha(theme.palette.primary.main, 0.2)}
                      strokeWidth="2"
                    />

                    {/* Connection lines - showing capability to connect */}
                    {targetRegions.map((location) => (
                      <line
                        key={`line-${location.id}`}
                        x1={hubLocation.x}
                        y1={hubLocation.y}
                        x2={location.x}
                        y2={location.y}
                        stroke={alpha(theme.palette.primary.main, 0.15)}
                        strokeWidth="1.5"
                        strokeDasharray="4,4"
                      />
                    ))}

                    {/* Central Hub */}
                    <circle
                      cx={hubLocation.x}
                      cy={hubLocation.y}
                      r="10"
                      fill={theme.palette.primary.main}
                      stroke="white"
                      strokeWidth="3"
                    />

                    {/* Service Location Points */}
                    {targetRegions.map((location) => {
                      const isSelected = selectedRegion === location.id;
                      return (
                        <g key={location.id}>
                          <circle
                            cx={location.x}
                            cy={location.y}
                            r={isSelected ? 12 : 8}
                            fill={
                              isSelected
                                ? theme.palette.secondary.main
                                : alpha(theme.palette.primary.main, 0.8)
                            }
                            stroke="white"
                            strokeWidth="2"
                            style={{
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onClick={() => handleLocationClick(location)}
                          />

                          {/* Pulse animation for selected region */}
                          {isSelected && (
                            <circle
                              cx={location.x}
                              cy={location.y}
                              r="20"
                              fill="none"
                              stroke={theme.palette.secondary.main}
                              strokeWidth="2"
                              opacity="0.6"
                              style={{
                                animation: "pulse 2s infinite",
                              }}
                            />
                          )}
                        </g>
                      );
                    })}
                  </svg>

                  {/* Hub Label */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: (hubLocation.x / 800) * 100 - 5 + "%",
                      top: (hubLocation.y / 400) * 100 - 10 + "%",
                      background: theme.palette.primary.main,
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    LinXar Labs
                  </Box>

                  <style jsx>{`
                    @keyframes pulse {
                      0% {
                        transform: scale(1);
                        opacity: 0.6;
                      }
                      50% {
                        transform: scale(1.5);
                        opacity: 0.2;
                      }
                      100% {
                        transform: scale(1);
                        opacity: 0.6;
                      }
                    }
                  `}</style>
                </Box>

                {/* Region Selector */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "center",
                  }}
                >
                  {targetRegions.map((region) => (
                    <Chip
                      key={region.id}
                      label={region.name}
                      onClick={() => handleLocationClick(region)}
                      variant={
                        selectedRegion === region.id ? "filled" : "outlined"
                      }
                      color={
                        selectedRegion === region.id ? "primary" : "default"
                      }
                      sx={{
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Region Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            {selectedRegion && (
              <Card
                sx={{
                  height: "100%",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.background.paper,
                    0.95
                  )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 12px 40px ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                  },
                }}
              >
                <CardContent sx={{ p: 4, height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    >
                      <Public sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {getRegionDetails(selectedRegion)?.name}
                      </Typography>
                      <Chip
                        label={getRegionDetails(selectedRegion)?.focus}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{ mb: 4, lineHeight: 1.7, color: "text.secondary" }}
                  >
                    {getRegionDetails(selectedRegion)?.description}
                  </Typography>

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      component={"p"}
                      gutterBottom
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "1rem",
                      }}
                    >
                      <Language sx={{ fontSize: 20 }} />
                      Communication:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {getRegionDetails(selectedRegion)?.languages.map(
                        (language) => (
                          <Chip
                            key={language}
                            label={language}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.3
                              ),
                              color: theme.palette.primary.main,
                            }}
                          />
                        )
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      component={"p"}
                      gutterBottom
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "1rem",
                      }}
                    >
                      <LocationOn sx={{ fontSize: 20 }} />
                      Coverage:
                    </Typography>
                    <Chip
                      label={`Timezone: ${
                        getRegionDetails(selectedRegion)?.timezone
                      }`}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: alpha(theme.palette.secondary.main, 0.3),
                        color: theme.palette.secondary.main,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      p: 3,
                      background: alpha(theme.palette.primary.main, 0.03),
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                        color: "text.secondary",
                        textAlign: "center",
                      }}
                    >
                      &quot;Our remote-first approach allows us to serve clients
                      in this region with seamless communication and dedicated
                      support&quot;
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GlobalReachSection;
